import '@stimulus/polyfills'
import { Application, Controller } from 'stimulus'
import './component.scss'

const app = Application.start()

app.register(
  'preference-modal',
  class extends Controller {
    static targets = ['modal', 'sliderContainer', 'regionsSlide', 'prefecturesSlide', 'prefecturesList']

    connect() {
      // 初期スクロール位置を保存
      this.scrollPosition = 0
      // 現在の表示状態を管理
      this.currentSlide = 'regions'
      // 現在のこだわり条件を管理
      this.preferenceType = null
      this.preferenceValue = null
    }

    // こだわり条件を指定してモーダルを開く
    openWithPreference(event) {
      this.preferenceType = event.currentTarget.dataset.preferenceType
      this.preferenceValue = event.currentTarget.dataset.preferenceValue

      // こだわり条件の表示を更新
      this.updatePreferenceDisplay()

      // 都道府県リンクのhrefを更新
      this.updatePrefectureLinks()

      // モーダルを開く
      this.open()
    }

    // こだわり条件の表示を更新
    updatePreferenceDisplay() {
      const conditionElement = this.modalTarget.querySelector('.preference-modal__preference-condition')
      if (conditionElement) {
        const displayName = this.getPreferenceDisplayName(this.preferenceType, this.preferenceValue)
        if (displayName) {
          conditionElement.textContent = displayName
          conditionElement.classList.add('preference-modal__preference-condition--active')
        } else {
          conditionElement.classList.remove('preference-modal__preference-condition--active')
        }
      }
    }

    // こだわり条件の表示名を生成
    getPreferenceDisplayName(type, value) {
      if (!type || !value) return null

      if (type === 'wall_material') {
        switch (value) {
          case 'mortar': return '外壁材：モルタル'
          case 'siding': return '外壁材：サイディング'
          case 'tile': return '外壁材：タイル'
          case 'alc': return '外壁材：ALC'
        }
      } else if (type === 'work_request') {
        switch (value) {
          case 'longevity': return '工事の希望：長持ちをさせたい'
          case 'insulation': return '工事の希望：断熱・遮熱効果をつけたい'
          case 'dirt_resistant': return '工事の希望：汚れにくくしたい'
          case 'subsidy': return '工事の希望：助成金を使いたい'
        }
      }

      return `${type}：${value}`
    }

    // モーダルを開く
    open() {
      // スクロール位置を保存
      this.scrollPosition = window.pageYOffset

      // bodyのスクロールを無効化
      document.body.style.overflow = 'hidden'

      // 初期状態に戻す
      this.showRegions()

      // モーダル要素を表示
      const modal = this.modalTarget
      const overlay = modal.querySelector('.js-preference-modal-overlay')
      const content = modal.querySelector('.js-preference-modal-content')

      if (modal && overlay && content) {
        modal.classList.add('preference-modal--active')
        overlay.classList.add('preference-modal__overlay--active')
        content.classList.add('preference-modal__content--active')
      }
    }

    // モーダルを閉じる
    close() {
      const modal = this.modalTarget
      const overlay = modal.querySelector('.js-preference-modal-overlay')
      const content = modal.querySelector('.js-preference-modal-content')

      if (overlay && content) {
        overlay.classList.remove('preference-modal__overlay--active')
        content.classList.remove('preference-modal__content--active')

        // アニメーション終了後にモーダルを非表示
        setTimeout(() => {
          if (modal) {
            modal.classList.remove('preference-modal--active')
          }

          // bodyのスクロールを復元
          document.body.style.overflow = ''
          window.scrollTo(0, this.scrollPosition)
        }, 300)
      }
    }

    // 都道府県表示に遷移
    showPrefectures(event) {
      const regionId = event.currentTarget.dataset.regionId

      // 選択した地域の都道府県のみを表示
      this.displayPrefecturesForRegion(regionId)

      // 横スライドアニメーション
      this.regionsSlideTarget.classList.add('slide-to-prefectures')
      this.prefecturesSlideTarget.classList.add('slide-to-prefectures')

      this.currentSlide = 'prefectures'
    }

    // 地域選択に戻る
    showRegions() {
      // すべての都道府県グループを非表示
      const prefectureGroups = this.prefecturesListTarget.querySelectorAll('.preference-modal__prefecture-group')
      prefectureGroups.forEach(group => {
        group.classList.remove('preference-modal__prefecture-group--active')
      })

      // 横スライドアニメーションを元に戻す
      this.regionsSlideTarget.classList.remove('slide-to-prefectures')
      this.prefecturesSlideTarget.classList.remove('slide-to-prefectures')

      this.currentSlide = 'regions'
    }

    // 指定した地域の都道府県を表示
    displayPrefecturesForRegion(regionId) {
      // すべての都道府県グループを非表示
      const prefectureGroups = this.prefecturesListTarget.querySelectorAll('.preference-modal__prefecture-group')
      prefectureGroups.forEach(group => {
        group.classList.remove('preference-modal__prefecture-group--active')
      })

      // 指定した地域の都道府県グループを表示
      const targetGroup = this.prefecturesListTarget.querySelector(`.preference-modal__prefecture-group[data-region-id="${regionId}"]`)
      if (targetGroup) {
        targetGroup.classList.add('preference-modal__prefecture-group--active')
      }
    }

    // 都道府県リンクのhrefとtracking_labelを更新
    updatePrefectureLinks() {
      const prefectureLinks = this.prefecturesListTarget.querySelectorAll('.preference-modal__prefecture-item')
      const queryParams = this.buildQueryParams()

      prefectureLinks.forEach(link => {
        const prefectureName = link.dataset.prefectureName
        const prefectureId = link.dataset.prefectureId
        if (!prefectureName || !prefectureId) return

        // ベースURLを構築
        let url = `/area/${prefectureName}/part/exterior_outer-wall`

        // クエリパラメータを追加
        if (queryParams) {
          url += `?${queryParams}`
        }

        // hrefを更新
        link.href = url

        // tracking_labelを更新
        const trackingLabel = this.buildTrackingLabel(prefectureId)
        if (trackingLabel) {
          link.dataset.trackingLabel = trackingLabel
        }
      })
    }

    // こだわり条件に基づいてクエリパラメータを構築
    buildQueryParams() {
      if (!this.preferenceType || !this.preferenceValue) {
        return null
      }

      let paramKey = ''
      let paramValue = ''

      if (this.preferenceType === 'wall_material') {
        paramKey = 'wall_materials[]'
        switch (this.preferenceValue) {
          case 'mortar': paramValue = '1'; break
          case 'siding': paramValue = '2'; break
          case 'tile': paramValue = '3'; break
          case 'alc': paramValue = '4'; break
        }
      } else if (this.preferenceType === 'work_request') {
        paramKey = 'themes[]'
        switch (this.preferenceValue) {
          case 'longevity': paramValue = '1'; break
          case 'insulation': paramValue = '2'; break
          case 'dirt_resistant': paramValue = '3'; break
          case 'subsidy': paramValue = '4'; break
        }
      }

      if (paramKey && paramValue) {
        return `${encodeURIComponent(paramKey)}=${encodeURIComponent(paramValue)}`
      }

      return null
    }

    // こだわり条件に基づいてtracking_labelを構築
    buildTrackingLabel(prefectureId) {
      if (!this.preferenceType || !this.preferenceValue || !prefectureId) {
        return null
      }

      let labelType = ''
      let labelValue = ''

      if (this.preferenceType === 'wall_material') {
        labelType = 'wallMaterial'
        switch (this.preferenceValue) {
          case 'mortar': labelValue = '1'; break
          case 'siding': labelValue = '2'; break
          case 'tile': labelValue = '3'; break
          case 'alc': labelValue = '4'; break
        }
      } else if (this.preferenceType === 'work_request') {
        labelType = 'theme'
        switch (this.preferenceValue) {
          case 'longevity': labelValue = '1'; break
          case 'insulation': labelValue = '2'; break
          case 'dirt_resistant': labelValue = '3'; break
          case 'subsidy': labelValue = '4'; break
        }
      }

      if (labelType && labelValue) {
        return `top-clientSearchModal-search-button-${labelType}_${labelValue}-${prefectureId}`
      }

      return null
    }
  }
)
